/**
 * Input Validation for AI Content Generation
 *
 * Tier 1: Rule-based validation that runs client-side (instant, free)
 * and is duplicated server-side as a safety net before the OpenAI call.
 *
 * Returns a score (0-100) and validity flag.
 * Score < 30 = reject (gibberish/nonsense)
 * Score 30-100 = proceed to generation
 */

// Common English words (top ~300 covering most casual/technical writing)
const COMMON_WORDS = new Set([
  'a', 'about', 'above', 'after', 'again', 'all', 'also', 'am', 'an', 'and',
  'any', 'app', 'api', 'are', 'as', 'at', 'aws', 'back', 'be', 'because',
  'been', 'before', 'being', 'best', 'better', 'between', 'big', 'both',
  'bug', 'build', 'built', 'but', 'by', 'came', 'can', 'change', 'check',
  'class', 'clean', 'cloud', 'code', 'come', 'could', 'create', 'css',
  'custom', 'data', 'day', 'debug', 'deploy', 'design', 'dev', 'did', 'do',
  'docker', 'does', 'done', 'down', 'each', 'easy', 'end', 'error', 'even',
  'every', 'example', 'experience', 'fast', 'feature', 'few', 'file', 'find',
  'first', 'fix', 'fixed', 'for', 'found', 'from', 'front', 'full', 'function',
  'get', 'git', 'go', 'going', 'good', 'got', 'great', 'had', 'handle',
  'has', 'have', 'having', 'he', 'help', 'her', 'here', 'high', 'him', 'his',
  'how', 'http', 'i', 'idea', 'if', 'implement', 'import', 'improve', 'in',
  'into', 'is', 'issue', 'it', 'its', 'java', 'js', 'just', 'keep', 'key',
  'know', 'last', 'learn', 'learned', 'let', 'like', 'line', 'linux', 'list',
  'long', 'look', 'made', 'main', 'make', 'many', 'may', 'me', 'method',
  'might', 'model', 'more', 'most', 'much', 'my', 'need', 'new', 'next',
  'no', 'node', 'not', 'now', 'of', 'off', 'old', 'on', 'one', 'only',
  'open', 'or', 'other', 'our', 'out', 'over', 'own', 'page', 'part',
  'pattern', 'people', 'performance', 'platform', 'post', 'problem', 'process',
  'production', 'project', 'push', 'put', 'python', 'query', 'question',
  'react', 'read', 'real', 'really', 'refactor', 'release', 'rest', 'right',
  'run', 'rust', 'same', 'save', 'say', 'scale', 'see', 'server', 'service',
  'set', 'share', 'she', 'should', 'show', 'simple', 'since', 'so', 'software',
  'solution', 'solve', 'solved', 'some', 'something', 'sql', 'start', 'state',
  'still', 'stop', 'such', 'system', 'take', 'team', 'tech', 'test', 'testing',
  'than', 'that', 'the', 'their', 'them', 'then', 'there', 'these', 'they',
  'thing', 'think', 'this', 'those', 'through', 'time', 'tip', 'to', 'today',
  'too', 'tool', 'top', 'try', 'type', 'under', 'up', 'update', 'us', 'use',
  'used', 'using', 'very', 'want', 'was', 'way', 'we', 'web', 'well', 'went',
  'were', 'what', 'when', 'where', 'which', 'while', 'who', 'why', 'will',
  'with', 'work', 'working', 'world', 'would', 'write', 'year', 'yet', 'you',
  'your', 'database', 'frontend', 'backend', 'stack', 'component', 'hook',
  'async', 'await', 'promise', 'callback', 'event', 'request', 'response',
  'endpoint', 'cache', 'memory', 'thread', 'container', 'kubernetes', 'ci',
  'cd', 'pipeline', 'migration', 'schema', 'index', 'optimization', 'latency',
  'throughput', 'microservice', 'monolith', 'architecture', 'library',
  'framework', 'package', 'dependency', 'version', 'branch', 'merge',
  'commit', 'review', 'pr', 'pull', 'request', 'agile', 'sprint', 'ticket',
  'monitor', 'log', 'alert', 'crash', 'trace', 'profile', 'benchmark',
  'typescript', 'javascript', 'golang', 'swift', 'kotlin', 'ruby', 'php',
  'csharp', 'cpp', 'html', 'nextjs', 'vue', 'angular', 'svelte', 'tailwind',
  'graphql', 'grpc', 'websocket', 'oauth', 'jwt', 'token', 'auth',
  'authentication', 'authorization', 'encryption', 'security', 'vulnerability',
  'wrote', 'writing', 'blog', 'article', 'tutorial', 'guide', 'lesson',
  'insight', 'approach', 'strategy', 'tradeoff', 'decision', 'thought',
  'opinion', 'debate', 'discussion', 'feedback', 'morning', 'yesterday',
  'recently', 'experience', 'interesting', 'challenging', 'difficult',
  'figured', 'discovered', 'realized', 'noticed', 'struggling', 'finally',
  'managed', 'able', 'trying', 'tried', 'attempt', 'success', 'failed',
  'failure', 'improve', 'improved', 'broke', 'broken', 'breaking', 'resolved',
]);

/**
 * Check if a string looks like a code snippet
 */
function looksLikeCode(text) {
  const codeIndicators = [
    /[{}();]/, // braces, parens, semicolons
    /=>/, // arrow functions
    /const |let |var |function |class |import |export |return /,
    /def |if |elif |else:|for |while |try:|except/,  // python
    /SELECT |FROM |WHERE |INSERT |UPDATE |DELETE /i,  // SQL
    /\.\w+\(/, // method calls
    /^\s*(\/\/|#|\/\*|\*)/m, // comments
  ];
  return codeIndicators.filter(re => re.test(text)).length >= 2;
}

/**
 * Calculate character-level entropy (Shannon entropy)
 * High entropy with low word ratio = likely random characters
 */
function charEntropy(text) {
  const freq = {};
  const clean = text.toLowerCase().replace(/\s/g, '');
  if (clean.length === 0) return 0;
  for (const ch of clean) {
    freq[ch] = (freq[ch] || 0) + 1;
  }
  let entropy = 0;
  for (const ch in freq) {
    const p = freq[ch] / clean.length;
    entropy -= p * Math.log2(p);
  }
  return entropy;
}

/**
 * Extract words from text (lowercase, alpha-only)
 */
function extractWords(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 0);
}

/**
 * Validate input for AI content generation
 *
 * @param {string} roughIdea - The user's input text
 * @param {string} [codeSnippet] - Optional code snippet
 * @returns {{ valid: boolean, score: number, reason?: string }}
 */
export function validateInput(roughIdea, codeSnippet = '') {
  const text = (roughIdea || '').trim();
  const hasCode = !!codeSnippet?.trim() || looksLikeCode(text);

  // Empty input
  if (!text && !codeSnippet?.trim()) {
    return { valid: false, score: 0, reason: 'Please enter an idea or paste a code snippet to generate a post.' };
  }

  // Code-only input is valid (code IS the content)
  if (!text && codeSnippet?.trim()) {
    return { valid: true, score: 60, reason: null };
  }

  const words = extractWords(text);
  const uniqueWords = new Set(words);

  // Too short (unless code present)
  if (text.length < 10 && !hasCode) {
    return { valid: false, score: 5, reason: 'Too short — describe your idea in a bit more detail.' };
  }

  if (words.length < 3 && !hasCode) {
    return { valid: false, score: 10, reason: 'Try adding more detail about what you want to share.' };
  }

  // --- Scoring system (0-100) ---
  let score = 50; // start neutral
  let reasons = [];

  // 1. Dictionary word ratio
  const knownWords = words.filter(w => COMMON_WORDS.has(w));
  const wordRatio = words.length > 0 ? knownWords.length / words.length : 0;

  if (wordRatio >= 0.6) {
    score += 20;
  } else if (wordRatio >= 0.4) {
    score += 10;
  } else if (wordRatio < 0.2 && !hasCode) {
    score -= 30;
    reasons.push('Most words don\'t seem to be recognizable.');
  } else if (wordRatio < 0.4 && !hasCode) {
    score -= 15;
  }

  // 2. Repetition check
  if (uniqueWords.size > 0 && words.length > 0) {
    const uniqueRatio = uniqueWords.size / words.length;
    if (uniqueRatio < 0.4 && words.length > 3) {
      score -= 35;
      reasons.push('Input seems repetitive.');
    } else if (uniqueRatio < 0.5 && words.length > 5) {
      score -= 10;
    }
  }

  // 3. Character entropy (random gibberish has high entropy + low word ratio)
  const entropy = charEntropy(text);
  if (entropy > 4.0 && wordRatio < 0.3 && !hasCode) {
    score -= 20;
    reasons.push('Input looks like random characters.');
  }

  // 4. Unique word count bonus/penalty
  if (uniqueWords.size < 4 && !hasCode) {
    score -= 15; // Very few unique words — likely not a real idea
  }
  if (uniqueWords.size >= 5) score += 10;
  if (uniqueWords.size >= 10) score += 10;

  // 5. Technical substance check — penalize inputs that are all filler with no topic
  const FILLER_WORDS = new Set(['i', 'am', 'is', 'are', 'the', 'a', 'an', 'this', 'that', 'it', 'my', 'we', 'they', 'he', 'she', 'do', 'did', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'will', 'would', 'could', 'should', 'can', 'may', 'might', 'hello', 'world', 'hi', 'hey', 'ok', 'okay', 'yes', 'no', 'again', 'test', 'testing', 'feature', 'thing', 'stuff', 'something']);
  const substantiveWords = words.filter(w => !FILLER_WORDS.has(w));
  if (substantiveWords.length === 0 && !hasCode) {
    score -= 30;
    if (reasons.length === 0) reasons.push('Your input doesn\'t contain a clear topic. Try describing something you built, learned, or want to share.');
  } else if (substantiveWords.length < 2 && !hasCode) {
    score -= 20;
    if (reasons.length === 0) reasons.push('Try being more specific about what you want to share.');
  } else if (substantiveWords.length < 3 && words.length <= 6 && !hasCode) {
    score -= 10; // Short input with minimal substance
  }

  // 6. Code presence bonus
  if (hasCode) score += 15;

  // 7. Length bonus (longer inputs are more likely intentional)
  if (text.length > 50) score += 5;
  if (text.length > 150) score += 5;

  // Clamp score
  score = Math.max(0, Math.min(100, score));

  const valid = score >= 30;
  const reason = valid
    ? null
    : (reasons.length > 0
      ? reasons[0]
      : 'Your input doesn\'t seem like a post idea. Try describing something you built, learned, or want to share.');

  return { valid, score, reason };
}

export default { validateInput, looksLikeCode, charEntropy, extractWords, COMMON_WORDS };
